import { useGetSections } from 'pages/documents_v2/hooks/useGetSections';
import { Fragment, useContext } from 'react';
import Parser from 'html-react-parser';
import HelperFunctions from 'pages/global/HelperFunctions';
import { useGetDocument } from 'pages/documents_v2/hooks/useGetDocument';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { DangerButton, SuccessButton } from 'components/Buttons';
import { HandThumbsDownFill, HandThumbsUpFill } from 'react-bootstrap-icons';
import { useFormikContext } from 'formik';
import InteractivePart from 'pages/documents/misc/_BlockParser/InteractivePart';
import { EditAreaContext } from 'pages/documents_v2/views/edit_area/context/EditAreaContext';

export function RenderedInteractiveContent({ block, content }) {
    const { t } = useTranslation();
    const document = useGetDocument();
    const sections = useGetSections(document?.id, true);
    const { values, setFieldValue } = useFormikContext();
    const { selectedPart, setSelectedPart } = useContext(EditAreaContext);

    if (content === undefined || content === '') {
        return null;
    }

    return (
        <>
            {Parser(content, {
                replace: replaceContent,
            })}
        </>
    );

    function replaceContent(domNode) {
        {
            if (
                domNode.name === 'change' &&
                domNode.hasOwnProperty('attribs') &&
                domNode.attribs.hasOwnProperty('data-diff-id')
            ) {
                const domNodeId = parseInt(domNode.attribs['data-diff-id']);
                let part = findChange(domNodeId);

                if (part === undefined) {
                    return <Fragment />;
                }

                const popover = (
                    <Popover id={`part-${part.id}-popover`}>
                        <Popover.Content>
                            <SuccessButton
                                size="sm"
                                className="mr-2"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    processSelectedChange('accept', part);
                                }}
                            >
                                <HandThumbsUpFill size={14} />
                                {t('documents:document.navbar.main.editor.navbar.btn.accept')}
                            </SuccessButton>

                            <DangerButton
                                size="sm"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    processSelectedChange('reject', part);
                                }}
                            >
                                <HandThumbsDownFill size={14} />
                                {t('documents:document.navbar.main.editor.navbar.btn.reject')}
                            </DangerButton>
                        </Popover.Content>
                    </Popover>
                );

                part = {
                    ...part,
                    oldText: HelperFunctions.prepareRefs(part.oldText, sections),
                    newText: HelperFunctions.prepareRefs(part.newText, sections),
                };

                return (
                    <OverlayTrigger
                        show={selectedPart === part.id}
                        trigger="click"
                        overlay={popover}
                        placement="bottom"
                    >
                        <InteractivePart
                            isSelected={selectedPart === part.id}
                            part={part}
                            selectPart={() => setSelectedPart(selectedPart === part.id ? undefined : part.id)}
                        />
                    </OverlayTrigger>
                );
            } else if (domNode.name === 'change') {
                return <Fragment />;
            }
        }
    }

    function findChange(diffId) {
        // Processed changes are stored in the form values, find them first
        const change = HelperFunctions.getByValue(values.changes, 'diffId', diffId);

        if (change) {
            return change;
        }

        // Find change in block
        return HelperFunctions.getByValue(block.changes, 'diffId', diffId);
    }

    function processSelectedChange(action, part) {
        const newChanges = [...values.changes].concat([
            {
                ...part,
                blockId: block.id,
                processed: true,
                accepted: action === 'accept',
            },
        ]);

        setSelectedPart(undefined);
        setFieldValue('changes', newChanges);
    }
}
